import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  List, ListItemButton, ListItemText, Stack, Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { HistoryEntry } from '../hooks/use-undo-redo';

interface Props {
  readonly entries: readonly HistoryEntry[];
  /** Which entry the document is showing now. */
  readonly index: number;
  readonly onJumpTo: (index: number) => void;
}

/**
 * Every edit made since the plan was opened, and a way back to any of them.
 *
 * Undo already exists, and for one step back it is the better tool. This is for the other
 * case: an admin who has been drawing for twenty minutes, has realised something went wrong
 * a while ago, and cannot tell how many times to press Ctrl+Z to get there. Pressing it
 * repeatedly and watching is how the redo trail gets thrown away by the next edit.
 *
 * Newest first, because that is the end being worked at. The last row is always the plan as
 * it was opened, which is the state every undo eventually leads back to.
 */
const HistoryPanel = ({ entries, index, onJumpTo }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const currentRef = useRef<HTMLDivElement | null>(null);

  // Keeps the step being shown in view as edits are made and as the cursor moves. A history
  // that has scrolled off is a list of edits with no indication of where you are in it.
  useEffect(() => {
    // Called defensively: jsdom has no layout and so no `scrollIntoView`, and a panel that
    // throws in every test that renders it is a panel nobody can assert anything about.
    currentRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [index]);

  return (
    <Stack sx={{ p: 1, minHeight: 0 }}>
      <Typography variant="caption" color={StyleConstants.GREY_COLOR} sx={{ px: 1 }}>
        {t('floorplan-editor-history-hint')}
      </Typography>

      <List dense sx={{ overflowY: 'auto' }}>
        {entries.map((entry, entryIndex) => {
          const current = entryIndex === index;
          // Anything after the cursor is a step that has been undone: still reachable, but
          // about to be thrown away by the next edit, so it is drawn as the future it is.
          const undone = entryIndex > index;

          return (
            <ListItemButton
              // Position in the history is the identity here: two consecutive moves produce
              // two rows with the same label, and they are genuinely different steps.
              // eslint-disable-next-line react/no-array-index-key
              key={`${entryIndex}-${entry.labelKey}`}
              ref={current ? currentRef : undefined}
              selected={current}
              onClick={(): void => onJumpTo(entryIndex)}
              aria-current={current ? 'step' : undefined}
              sx={{ borderRadius: 1, opacity: undone ? 0.5 : 1 }}
            >
              <ListItemText
                primaryTypographyProps={{ variant: 'body2' }}
                primary={String(t(entry.labelKey, { count: entry.count ?? 1 }))}
                secondaryTypographyProps={{ variant: 'caption' }}
                secondary={current ? String(t('floorplan-editor-history-current')) : undefined}
              />
            </ListItemButton>
          );
        }).reverse()}
      </List>
    </Stack>
  );
};

export default HistoryPanel;
